import { defaultContainer } from '../container-instance.class.mjs';
import { SERVICE_METADATA_DEFAULTS } from '../constants/service-defaults.const.mjs';
import { wrapDependencyAsResolvable } from '../utils/wrap-resolvable-dependency.mjs';
import { isArray } from '../utils/is-array.util.mjs';
import { NativeError } from '../constants/minification/native-error.const.mjs';
import { NativeNull } from '../constants/minification/native-null.const.mjs';
export function Service(optionsOrDependencies, maybeDependencies) {
    return targetConstructor => {
        if (optionsOrDependencies == NativeNull || targetConstructor == NativeNull) {
            throw NativeError('The @Service decorator was not used correctly.');
        }
        /** A list of dependencies resolved from the arguments provided to the function. */
        let resolvedDependencies;
        const optionsOrDependenciesIsArray = isArray(optionsOrDependencies);
        if (optionsOrDependenciesIsArray) {
            /**
             * If our first argument is an array, then the user has not specified any options,
             * and has instead filled the slot with a list of dependencies.
             */
            resolvedDependencies = optionsOrDependencies;
        }
        else if (isArray(maybeDependencies)) {
            resolvedDependencies = maybeDependencies;
        }
        else if ('dependencies' in optionsOrDependencies) {
            /**
             * The first argument is of type `ServiceOptions<T>` with dependencies.
             * We can access its "dependencies" property and then map the unwrapped types.
             */
            resolvedDependencies = optionsOrDependencies.dependencies;
        }
        if (!resolvedDependencies) {
            /** At this point we have exhausted all options, so throw. */
            throw NativeError('The dependencies provided were not able to be resolved.');
        }
        /**
         * A default list of options for this service.
         * These are used when the options are not explicitly provided to the function.
         * However, if options are passed, these are merged in as defaults.
         */
        const metadata = {
            id: targetConstructor,
            type: targetConstructor,
            ...SERVICE_METADATA_DEFAULTS,
            container: defaultContainer,
        };
        if (!optionsOrDependenciesIsArray) {
            Object.assign(metadata, optionsOrDependencies);
        }
        const wrappedDependencies = resolvedDependencies.map((dependency, index) => wrapDependencyAsResolvable(dependency, metadata, index));
        const { id, container } = metadata;
        /**
         * If the target is already registered, `@Service` may have been called twice on the same class.
         * Alternatively, the consumer may be trying to override a well-known key in the container.
         *
         * Not throwing here may lead to confusion as, in the case of duplicate calls to `@Service` referencing
         * the same identifier, the "winning" implementation which is *actually* used may be unclear.
         *
         * We disable recursiveness in the check here to allow for sub-containers to override well-known
         * IDs in parent containers.
         * This makes it easier to immutably extend containers in-place by way of sub-containers.
         *
         * One final note: It's also very important to ensure that the duplication checks are *not* performed
         * if the `multiple` bit is set to true.  In that case, we'd very much want it to function like an
         * ordinary `Container.set` call, wherein the same operation would result in this service being added
         * to an internal array of values for the specified identifier.
         */
        if (container.has(id, false) && !metadata.multiple) {
            throw NativeError(`@Service() has been called twice upon ${targetConstructor.name}, or you have used an ID twice.`);
        }
        /**
         * The `.set(Omit<ServiceOptions<unknown>, "dependencies">, TypeWrapper[])` overload is used here.
         * TypeScript downcasts the metadata to the type above.
         *
         * By default, this is set to `defaultContainer`.
         * Therefore, it will be bound to that.
         */
        container.set(metadata, wrappedDependencies);
    };
}
//# sourceMappingURL=service.decorator.mjs.map